import React, { useState } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import { formService } from '../../services/formService';
import type { FormField, FormSummary } from '../../types/form';
import {
  buildContactBindingSettingsJson,
  buildMaskSettingsJson,
  type FormFieldContactBinding,
  type FormFieldMaskKind,
} from '../../types/formFieldSettings';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

type DraftField = {
  key: string;
  title: string;
  required: boolean;
  maskKind: FormFieldMaskKind;
  contactBinding: FormFieldContactBinding;
};

const IDENTIFICATION_FIELDS: DraftField[] = [
  {
    key: 'name',
    title: 'Nome',
    required: true,
    maskKind: 'None',
    contactBinding: 'Name',
  },
  {
    key: 'email',
    title: 'E-mail',
    required: true,
    maskKind: 'None',
    contactBinding: 'Email',
  },
  {
    key: 'phone',
    title: 'Telefone',
    required: false,
    maskKind: 'Phone',
    contactBinding: 'Phone',
  },
];

function createEmptyDraftField(): DraftField {
  return {
    key: `field-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: '',
    required: true,
    maskKind: 'None',
    contactBinding: 'None',
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (summary: FormSummary) => void;
  addToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
};

export const InboxCreateSupportFormModal: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  addToast,
}) => {
  const [title, setTitle] = useState('Identificação do visitante');
  const [description, setDescription] = useState('Antes de começar, como podemos te chamar?');
  const [fields, setFields] = useState<DraftField[]>(IDENTIFICATION_FIELDS.map((f) => ({ ...f })));
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setTitle('Identificação do visitante');
    setDescription('Antes de começar, como podemos te chamar?');
    setFields(IDENTIFICATION_FIELDS.map((f) => ({ ...f })));
  };

  const handleClose = () => {
    if (isSaving)
    {
      return;
    }

    reset();
    onClose();
  };

  const handleCreate = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle)
    {
      addToast('Informe o título do formulário.', 'error');
      return;
    }

    const validFields = fields.filter((field) => field.title.trim());
    if (validFields.length === 0)
    {
      addToast('Inclua ao menos um campo.', 'error');
      return;
    }

    const bindings = validFields
      .map((field) => field.contactBinding)
      .filter((binding) => binding !== 'None');
    if (new Set(bindings).size !== bindings.length)
    {
      addToast('Cada vínculo com o contato só pode aparecer uma vez.', 'error');
      return;
    }

    try
    {
      setIsSaving(true);
      const created = await formService.create({
        title: trimmedTitle,
        description: description.trim() || undefined,
        purpose: 'Support',
      });

      const sectionId = created.sections[0]?.id;
      if (!sectionId)
      {
        throw new Error('Formulário criado sem seção.');
      }

      const nextFields: FormField[] = validFields.map((field, order) => {
        let settingsJson: string | undefined;
        if (field.maskKind !== 'None')
        {
          settingsJson = buildMaskSettingsJson(field.maskKind, undefined, settingsJson);
        }

        if (field.contactBinding !== 'None')
        {
          settingsJson = buildContactBindingSettingsJson(field.contactBinding, settingsJson);
        }

        return {
          id: crypto.randomUUID?.() ?? `field-${order}-${Date.now()}`,
          sectionId,
          title: field.title.trim(),
          type: 'ShortText',
          isRequired: field.required,
          order,
          goToSectionBasedOnAnswer: false,
          options: [],
          settingsJson,
        };
      });

      await formService.update(created.id, {
        title: trimmedTitle,
        description: description.trim() || undefined,
        purpose: 'Support',
        accessType: 'Public',
        sections: created.sections,
        fields: nextFields,
      });

      const published = await formService.publish(created.id);
      onCreated({
        id: published.id,
        title: published.title,
        status: published.status,
        accessType: published.accessType,
        purpose: published.purpose || 'Support',
        acceptingResponses: published.acceptingResponses,
        responseCount: 0,
        updatedAt: published.updatedAt,
        publicId: published.publicId,
      });
      addToast('Formulário criado e vinculado.', 'success');
      reset();
      onClose();
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Não foi possível criar o formulário.'), 'error');
    }
    finally
    {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Criar formulário de atendimento"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Cria um formulário público do módulo Forms, com finalidade Atendimento.
          Para seções, arquivo e branching, abra o construtor completo depois.
        </p>
        <div>
          <label className="mb-1 block text-sm text-gray-700">Título</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-700">Mensagem / descrição</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            rows={2}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setFields(IDENTIFICATION_FIELDS.map((f) => ({ ...f })))}
          >
            Atalho: Identificação
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setFields((prev) => [...prev, createEmptyDraftField()])}
          >
            Adicionar campo
          </Button>
        </div>
        <ul className="space-y-3">
          {fields.map((field, index) => (
            <li
              key={field.key}
              className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-gray-500">Campo {index + 1}</p>
                <button
                  type="button"
                  className="text-xs text-red-600 hover:underline"
                  onClick={() =>
                    setFields((prev) => prev.filter((item) => item.key !== field.key))
                  }
                >
                  Remover
                </button>
              </div>
              <input
                type="text"
                value={field.title}
                onChange={(e) =>
                  setFields((prev) =>
                    prev.map((item) =>
                      item.key === field.key ? { ...item, title: e.target.value } : item
                    )
                  )
                }
                placeholder="Rótulo"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) =>
                      setFields((prev) =>
                        prev.map((item) =>
                          item.key === field.key
                            ? { ...item, required: e.target.checked }
                            : item
                        )
                      )
                    }
                  />
                  Obrigatório
                </label>
                <select
                  value={field.maskKind}
                  onChange={(e) =>
                    setFields((prev) =>
                      prev.map((item) =>
                        item.key === field.key
                          ? { ...item, maskKind: e.target.value as FormFieldMaskKind }
                          : item
                      )
                    )
                  }
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                  <option value="None">Sem máscara</option>
                  <option value="Phone">Telefone</option>
                  <option value="Cpf">CPF</option>
                  <option value="Cnpj">CNPJ</option>
                  <option value="Cep">CEP</option>
                </select>
                <select
                  value={field.contactBinding}
                  onChange={(e) =>
                    setFields((prev) =>
                      prev.map((item) =>
                        item.key === field.key
                          ? {
                              ...item,
                              contactBinding: e.target.value as FormFieldContactBinding,
                            }
                          : item
                      )
                    )
                  }
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                  <option value="None">Sem vínculo</option>
                  <option value="Name">Nome</option>
                  <option value="Email">E-mail</option>
                  <option value="Phone">Telefone</option>
                </select>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type="button" variant="primary" onClick={() => void handleCreate()} disabled={isSaving}>
            {isSaving ? 'Criando...' : 'Criar e publicar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
